/**
 * Shared types, color maps, and formatting helpers for the GHL Qualifier
 * admin dashboard. Mirrors the shape of `lib/ghl-dashboard.ts`.
 */

import {
  formatRelativeTime as formatRelativeTimeShared,
  truncateId as truncateIdShared,
} from '@/lib/ghl-dashboard'

// Re-export shared helpers so callers can import from a single module.
export const formatRelativeTime = formatRelativeTimeShared
export const truncateId = truncateIdShared

// ---------------------------------------------------------------------------
// Status color maps
// ---------------------------------------------------------------------------

/**
 * Outcome pills for the Qualified Leads tab.
 *
 * `qualified_result` values written by the qualifier worker. We render
 * `pending` / `scheduled` rows under the "pending" outcome — operators don't
 * need to differentiate between "waiting in queue" and "currently processing"
 * at the feed level.
 */
export const QUALIFIER_OUTCOME_COLORS: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  qualified: { bg: 'bg-green-100', text: 'text-green-800', label: 'Qualified' },
  unqualified: { bg: 'bg-red-100', text: 'text-red-800', label: 'Unqualified' },
  pending: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Pending' },
  failed: { bg: 'bg-rose-100', text: 'text-rose-800', label: 'Failed' },
  cancelled: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Cancelled' },
}

export function getQualifierOutcomeColor(
  status: string,
  qualificationResult: string | null
): { bg: string; text: string; label: string } {
  // Failed / cancelled rows are surfaced regardless of qualification_result.
  if (status === 'failed') return QUALIFIER_OUTCOME_COLORS.failed
  if (status === 'cancelled') return QUALIFIER_OUTCOME_COLORS.cancelled
  if (qualificationResult === 'qualified') return QUALIFIER_OUTCOME_COLORS.qualified
  if (qualificationResult === 'unqualified') return QUALIFIER_OUTCOME_COLORS.unqualified
  return QUALIFIER_OUTCOME_COLORS.pending
}

/**
 * Status pills for the Converted Leads tab.
 */
export const CONVERTED_STATUS_COLORS: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  completed_converted: { bg: 'bg-green-100', text: 'text-green-800', label: 'Confirmed' },
  in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'In Progress' },
  scheduled: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Scheduled' },
  completed_exhausted: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Exhausted' },
  cancelled: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Cancelled' },
  failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
}

export function getConvertedStatusColor(status: string) {
  return (
    CONVERTED_STATUS_COLORS[status] ?? {
      bg: 'bg-gray-100',
      text: 'text-gray-600',
      label: status,
    }
  )
}

/**
 * Status pills for the Phone-Call Leads tab (sourced from
 * ghl_form_call_lead_checks).
 */
export const PHONE_CALL_STATUS_COLORS: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
  pending: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Pending' },
  processing: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Processing' },
  cancelled: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Cancelled' },
  failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
}

export function getPhoneCallStatusColor(status: string) {
  return (
    PHONE_CALL_STATUS_COLORS[status] ?? {
      bg: 'bg-gray-100',
      text: 'text-gray-600',
      label: status,
    }
  )
}

/**
 * Classification pattern color/label map. Mirrors the patterns the qualifier
 * worker writes to `classification_pattern`. Unknown patterns fall through
 * to a neutral gray pill so dashboards keep working when new patterns are
 * introduced server-side.
 */
/**
 * Maps the `classification_pattern` column value to a display badge.
 *
 * The qualifier worker writes one of these values:
 *   - 'rideshare_mindset' / 'non_user_statement' / 'price_rejection' /
 *     'use_case_mismatch' — when Claude detected a disqualifying signal
 *     and `qualified_no` was applied
 *   - 'no_signal' — when Claude reviewed the conversation and found nothing
 *     disqualifying (lead stays `qualified`, no extra tag)
 *
 * Legacy middleware-era keys (price_shopper, tire_kicker, explicit_decline,
 * etc.) are kept so any historical rows still render with a meaningful
 * label.
 */
export const QUALIFIER_PATTERN_COLORS: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  // Qualifier-emitted patterns (strong signals)
  rideshare_mindset: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    label: 'Rideshare Mindset',
  },
  non_user_statement: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    label: 'Non-User Statement',
  },
  price_rejection: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    label: 'Price Rejection',
  },
  use_case_mismatch: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    label: 'Use-Case Mismatch',
  },

  // Qualifier "no signal triggered" outcome — neutral muted badge
  no_signal: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    label: 'No signal',
  },

  // Legacy middleware-era patterns kept for historical rows
  price_shopper: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Price Shopper' },
  price_reject: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Price Reject' },
  tire_kicker: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Tire Kicker' },
  explicit_decline: { bg: 'bg-red-100', text: 'text-red-800', label: 'Explicit Decline' },
  geographic_mismatch: {
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    label: 'Geographic Mismatch',
  },
  wrong_service: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Wrong Service' },
  spam_or_test: { bg: 'bg-slate-200', text: 'text-slate-800', label: 'Spam / Test' },
  wrong_number: { bg: 'bg-slate-200', text: 'text-slate-800', label: 'Wrong Number' },
  sustained_non_engagement: {
    bg: 'bg-zinc-200',
    text: 'text-zinc-700',
    label: 'No Engagement',
  },

  // Fallback when pattern is null or unknown
  none: { bg: 'bg-gray-100', text: 'text-gray-600', label: '—' },
}

export function getQualifierPatternColor(pattern: string | null) {
  return (
    QUALIFIER_PATTERN_COLORS[pattern ?? 'none'] ??
    QUALIFIER_PATTERN_COLORS['none']
  )
}

/**
 * Field-write status → display config for the contact.google_ads_converted_leads_monetary
 * write attempt. Surfaced on the Confirmed lifecycle header so operators can
 * see at a glance whether the value landed, was skipped, or failed.
 *
 * The four operationally distinct outcomes:
 *   - success              → value written. Green check.
 *   - skipped_no_price     → Claude couldn't extract a price. Neutral.
 *   - skipped_already_set  → field had a prior value (idempotency). Neutral.
 *   - failed_field_missing → operator hasn't created the custom field. Red,
 *                            with explicit "create the field" guidance.
 *   - failed_api_error     → GHL API blew up. Red, with the error message.
 */
export const FIELD_WRITE_STATUS_DISPLAY: Record<
  string,
  {
    bg: string
    text: string
    border: string
    label: string
    icon: 'check' | 'skip' | 'error'
    severity: 'success' | 'neutral' | 'error'
  }
> = {
  success: {
    bg: 'bg-green-50',
    text: 'text-green-800',
    border: 'border-green-200',
    label: 'Written to GHL',
    icon: 'check',
    severity: 'success',
  },
  skipped_no_price: {
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    border: 'border-gray-200',
    label: 'Skipped — no price extracted',
    icon: 'skip',
    severity: 'neutral',
  },
  skipped_already_set: {
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    border: 'border-gray-200',
    label: 'Skipped — field already populated',
    icon: 'skip',
    severity: 'neutral',
  },
  skipped_already_converted: {
    bg: 'bg-blue-50',
    text: 'text-blue-800',
    border: 'border-blue-200',
    label: 'Skipped — contact already converted',
    icon: 'skip',
    severity: 'neutral',
  },
  failed_field_missing: {
    bg: 'bg-red-50',
    text: 'text-red-900',
    border: 'border-red-200',
    label: 'Failed — custom field not configured',
    icon: 'error',
    severity: 'error',
  },
  failed_api_error: {
    bg: 'bg-red-50',
    text: 'text-red-900',
    border: 'border-red-200',
    label: 'Failed — GHL API error',
    icon: 'error',
    severity: 'error',
  },
}

export function getFieldWriteStatusDisplay(status: string | null) {
  if (!status) return null
  return FIELD_WRITE_STATUS_DISPLAY[status] ?? null
}

/**
 * Per-step result for the field-write decision trace surfaced in the dashboard.
 *
 *   - 'passed'        — step ran and succeeded (green check)
 *   - 'failed'        — step ran and failed terminally (red X)
 *   - 'skipped_block' — step ran but blocked the next step intentionally
 *                       (e.g. idempotency guard — gray minus icon)
 *   - 'not_evaluated' — step never ran because an earlier step gated it
 *                       (gray dashed circle, muted text)
 */
export type FieldWriteTraceState =
  | 'passed'
  | 'failed'
  | 'skipped_block'
  | 'not_evaluated'

export interface FieldWriteTraceStep {
  readonly key: 'price_check' | 'catalogue_lookup' | 'existing_value_read' | 'write_call'
  readonly label: string
  readonly state: FieldWriteTraceState
  readonly detail?: string
}

/**
 * Reconstruct the per-step trace from the lifecycle row's stored status +
 * error message. The decision tree is deterministic, so every status uniquely
 * implies a 4-step trace.
 *
 * The `failed_api_error` variants store a prefix in field_write_error
 * (`catalogue_lookup_failed: ...`, `existing_value_read_failed: ...`,
 * `write_call_failed: ...`) so we can pinpoint exactly which API call failed.
 * Falls back to "write_call" as the assumed step if no prefix matches —
 * preserves backwards compatibility with rows written before the prefix was
 * added.
 *
 * Returns null when the lifecycle never reached the field-write code path
 * (status is null) OR when it took the tag-pre-check short-circuit
 * (`skipped_already_converted`) — those don't fit the 4-step shape.
 */
export function computeFieldWriteTrace(
  status: string | null,
  errorMessage: string | null,
  priceQuoted: number | null,
  valueWritten: number | null
): readonly FieldWriteTraceStep[] | null {
  if (!status) return null
  if (status === 'skipped_already_converted') return null

  // Helper: stripped detail text from a prefixed error message.
  function stripPrefix(prefix: string): string {
    if (!errorMessage) return ''
    if (errorMessage.startsWith(prefix)) {
      return errorMessage.slice(prefix.length).trim()
    }
    return errorMessage
  }

  // Identify which API call failed for failed_api_error variants.
  function failedStep(): 'catalogue_lookup' | 'existing_value_read' | 'write_call' {
    if (!errorMessage) return 'write_call'
    if (errorMessage.startsWith('catalogue_lookup_failed:')) return 'catalogue_lookup'
    if (errorMessage.startsWith('existing_value_read_failed:')) return 'existing_value_read'
    if (errorMessage.startsWith('write_call_failed:')) return 'write_call'
    // Legacy rows: error message wasn't prefixed. Assume write_call (most
    // common path that throws — that's where the actual PUT happens).
    return 'write_call'
  }

  const steps: FieldWriteTraceStep[] = []

  // ─── Step 1: Did Claude extract a price? ─────────────────────────────────
  if (status === 'skipped_no_price') {
    steps.push({
      key: 'price_check',
      label: 'Did Claude extract a price?',
      state: 'failed',
      detail: 'No — `price_quoted` was null. Nothing to write.',
    })
    steps.push({
      key: 'catalogue_lookup',
      label: 'Is the custom field configured on this location?',
      state: 'not_evaluated',
    })
    steps.push({
      key: 'existing_value_read',
      label: 'Does the contact already have a value?',
      state: 'not_evaluated',
    })
    steps.push({
      key: 'write_call',
      label: 'Write value to GHL',
      state: 'not_evaluated',
    })
    return steps
  }

  // All other paths cleared step 1.
  steps.push({
    key: 'price_check',
    label: 'Did Claude extract a price?',
    state: 'passed',
    detail: priceQuoted != null ? `Yes — $${priceQuoted}` : 'Yes',
  })

  // ─── Step 2: Catalogue lookup ────────────────────────────────────────────
  if (status === 'failed_field_missing') {
    steps.push({
      key: 'catalogue_lookup',
      label: 'Is the custom field configured on this location?',
      state: 'failed',
      detail:
        'No — the location\'s custom-field catalogue has no entry for ' +
        '`contact.google_ads_converted_leads_monetary`. Operator must create the ' +
        'field in GHL → Settings → Custom Fields → Contact.',
    })
    steps.push({
      key: 'existing_value_read',
      label: 'Does the contact already have a value?',
      state: 'not_evaluated',
    })
    steps.push({
      key: 'write_call',
      label: 'Write value to GHL',
      state: 'not_evaluated',
    })
    return steps
  }

  if (status === 'failed_api_error' && failedStep() === 'catalogue_lookup') {
    steps.push({
      key: 'catalogue_lookup',
      label: 'Is the custom field configured on this location?',
      state: 'failed',
      detail: `GHL API error: ${stripPrefix('catalogue_lookup_failed:')}`,
    })
    steps.push({
      key: 'existing_value_read',
      label: 'Does the contact already have a value?',
      state: 'not_evaluated',
    })
    steps.push({
      key: 'write_call',
      label: 'Write value to GHL',
      state: 'not_evaluated',
    })
    return steps
  }

  steps.push({
    key: 'catalogue_lookup',
    label: 'Is the custom field configured on this location?',
    state: 'passed',
    detail: 'Yes — `contact.google_ads_converted_leads_monetary` resolved in catalogue.',
  })

  // ─── Step 3: Existing value read ─────────────────────────────────────────
  if (status === 'failed_api_error' && failedStep() === 'existing_value_read') {
    steps.push({
      key: 'existing_value_read',
      label: 'Does the contact already have a value?',
      state: 'failed',
      detail: `GHL API error: ${stripPrefix('existing_value_read_failed:')}`,
    })
    steps.push({
      key: 'write_call',
      label: 'Write value to GHL',
      state: 'not_evaluated',
    })
    return steps
  }

  if (status === 'skipped_already_set') {
    steps.push({
      key: 'existing_value_read',
      label: 'Does the contact already have a value?',
      state: 'skipped_block',
      detail:
        'Yes — field already populated. Not overwritten ' +
        '(idempotency: a later booking should not clobber an earlier conversion).',
    })
    steps.push({
      key: 'write_call',
      label: 'Write value to GHL',
      state: 'not_evaluated',
    })
    return steps
  }

  steps.push({
    key: 'existing_value_read',
    label: 'Does the contact already have a value?',
    state: 'passed',
    detail: 'No — field was empty. Proceeding to write.',
  })

  // ─── Step 4: The write itself ────────────────────────────────────────────
  if (status === 'success') {
    steps.push({
      key: 'write_call',
      label: 'Write value to GHL',
      state: 'passed',
      detail:
        valueWritten != null
          ? `Success — wrote $${valueWritten} to contact.google_ads_converted_leads_monetary`
          : 'Success — value written to contact.google_ads_converted_leads_monetary',
    })
    return steps
  }

  if (status === 'failed_api_error') {
    steps.push({
      key: 'write_call',
      label: 'Write value to GHL',
      state: 'failed',
      detail: `GHL API error: ${stripPrefix('write_call_failed:')}`,
    })
    return steps
  }

  // Should be unreachable — every status is handled above.
  return steps
}

/**
 * Cancel-reason → human label map for the Cancelled status pill subtitle.
 */
export const CANCEL_REASON_LABELS: Record<string, string> = {
  contact_deleted: 'Contact deleted',
  tag_present_at_schedule: 'Tag already present',
  data_pipeline_disabled: 'Data pipeline disabled',
  qualifier_disabled: 'Qualifier disabled',
  opportunity_lost: 'Already marked lost in GoHighLevel',
}

export function formatCancelReason(reason: string | null): string {
  if (!reason) return '—'
  return CANCEL_REASON_LABELS[reason] ?? reason
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—'
  return CURRENCY_FORMATTER.format(value)
}

export function formatConfidence(confidence: number | null | undefined): string {
  if (confidence == null) return '—'
  return `${Math.round(confidence)}`
}

/**
 * Split an ISO timestamp into a human date + time for two-line table cells
 * (e.g. "Jun 4, 2026" / "2:30 PM"). Returns null for missing/invalid input so
 * callers can render a "—" fallback. Uses the viewer's locale.
 */
export function formatDateTimeParts(
  iso: string | null | undefined
): { date: string; time: string } | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return {
    date: d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  }
}

/**
 * Tailwind text color class for confidence numbers, per the wireframe spec:
 * ≥80 green, 60–79 yellow, <60 red. Returns a class fragment usable in JSX.
 */
export function getConfidenceColorClass(confidence: number | null | undefined): string {
  if (confidence == null) return 'text-gray-500'
  if (confidence >= 80) return 'text-green-700'
  if (confidence >= 60) return 'text-yellow-700'
  return 'text-red-700'
}

/**
 * Build the GHL contact deep-link the operator needs to triage a row.
 */
export function buildGHLContactUrl(locationId: string, contactId: string): string {
  return `https://app.gohighlevel.com/v2/location/${locationId}/contacts/detail/${contactId}`
}

/**
 * Tab values for the Location Detail page. Kept here so the page + redirect
 * components share a single source of truth.
 */
export const QUALIFIER_DETAIL_TABS = [
  'qualified-leads',
  'converted-leads',
  'phone-call-leads',
] as const
export type QualifierDetailTab = (typeof QUALIFIER_DETAIL_TABS)[number]
export const QUALIFIER_DETAIL_DEFAULT_TAB: QualifierDetailTab = 'qualified-leads'

export function isQualifierDetailTab(value: string | undefined): value is QualifierDetailTab {
  return typeof value === 'string' && (QUALIFIER_DETAIL_TABS as readonly string[]).includes(value)
}
